import React, { useState, useEffect } from 'react';
import './App.css';
import Layout from './components/Layout';
import ReceiptUpload from './components/ReceiptUpload';
import StatementUpload from './components/StatementUpload';
import QuestionPanel from './components/QuestionPanel';
import ExpenseList from './components/ExpenseList';
import ManualEntry from './components/ManualEntry';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminPanel from './pages/AdminPanel';
import InboxPage from './pages/InboxPage';
import SettingsPage from './pages/SettingsPage';
import TelegramPage from './pages/TelegramPage';
import PrivacyPage from './pages/PrivacyPage';
import CurrencyRatesDashboard from './components/CurrencyRatesDashboard';
import FeedbackPage from './pages/FeedbackPage';
import ReceiptsPage from './pages/ReceiptsPage';
import EmailForwardingPage from './pages/EmailForwardingPage';
import ReadmePage from './pages/ReadmePage';
import Auth from './components/Auth';
import TelegramEmailPrompt from './components/TelegramEmailPrompt';
import MergeConfirmModal from './components/MergeConfirmModal';
import { checkHealth } from './services/api';
import { linkEmail, mergeAccounts } from './services/settingsApi';
import { useAuth } from './context/AuthContext';
import { isAdmin } from './utils/admin';
import { setupGlobalErrorHandler } from './utils/errorLogger';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  const { user, loading, isTelegramOnlyUser, getAccessToken } = useAuth();
  const [activeTab, setActiveTab] = useState('upload'); // Default to Upload Receipt for mobile-first experience
  const [serverStatus, setServerStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [newExpenseIds, setNewExpenseIds] = useState(new Set());
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [mergeState, setMergeState] = useState(null); // { conflictEmail, mergeToken }

  useEffect(() => {
    // Setup global error handler on app mount
    setupGlobalErrorHandler();
  }, []);

  useEffect(() => {
    // Show email prompt for telegram-only users who haven't dismissed it
    if (user && isTelegramOnlyUser && !TelegramEmailPrompt.isDismissed()) {
      setShowEmailPrompt(true);
    } else {
      setShowEmailPrompt(false);
    }
  }, [user, isTelegramOnlyUser]);

  const handleLinkEmail = async (email) => {
    const token = getAccessToken();
    const result = await linkEmail(email, token);
    if (result.conflict) {
      // Show merge confirmation modal
      setShowEmailPrompt(false);
      setMergeState({ conflictEmail: result.conflictEmail, mergeToken: result.mergeToken });
      return;
    }
    setShowEmailPrompt(false);
  };

  const handleMergeConfirm = async () => {
    if (!mergeState?.mergeToken) return;
    const token = getAccessToken();
    await mergeAccounts(mergeState.mergeToken, token);
    setMergeState(null);
    // Force page reload to refresh user state after merge
    window.location.reload();
  };

  useEffect(() => {
    // Check server health on mount
    if (user) {
      let isMounted = true;
      checkHealth()
        .then((health) => {
          if (!isMounted) return;
          setServerStatus(health.status === 'offline' ? 'offline' : 'online');
        })
        .catch(() => {
          if (!isMounted) return;
          setServerStatus('offline');
        });

      return () => {
        isMounted = false;
      };
    }
  }, [user]);

  const handleExpenseAdded = (expense) => {
    // Track the new expense ID to mark it as "new" in the list
    if (expense && expense.id) {
      setNewExpenseIds(prev => new Set([...prev, expense.id]));
    }
    // Trigger expense list refresh
    setRefreshTrigger(prev => prev + 1);
    // Switch to expenses tab to see the new expense
    setActiveTab('expenses');
  };

  const clearNewExpenseIds = () => {
    setNewExpenseIds(new Set());
  };

  const handleStatementUploaded = () => {
    // Refresh expenses list without navigating away
    setRefreshTrigger(prev => prev + 1);
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#F9FAFB'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #E5E7EB',
            borderTopColor: '#3B82F6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth screen if not logged in
  if (!user) {
    return <Auth />;
  }

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'upload':
        return <ReceiptUpload onExpenseAdded={handleExpenseAdded} onBack={() => setActiveTab('expenses')} />;
      case 'statement':
        return <StatementUpload onExpenseAdded={handleStatementUploaded} onBack={() => setActiveTab('expenses')} />;
      case 'manual':
        return <ManualEntry onExpenseAdded={handleExpenseAdded} onBack={() => setActiveTab('expenses')} />;
      case 'ask':
        return <QuestionPanel />;
      case 'expenses':
        return <ExpenseList refreshTrigger={refreshTrigger} newExpenseIds={newExpenseIds} onClearNewExpenseIds={clearNewExpenseIds} />;
      case 'receipts':
        return <ReceiptsPage />;
      case 'email-forwarding':
        return <EmailForwardingPage />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'readme':
        return <ReadmePage />;
      case 'currency':
        return <CurrencyRatesDashboard />;
      case 'feedback':
        return <FeedbackPage />;
      case 'settings':
        return <SettingsPage />;
      case 'telegram':
        return <TelegramPage />;
      case 'privacy':
        return <PrivacyPage />;
      case 'inbox':
        return isAdmin(user) ? <InboxPage /> : <ExpenseList refreshTrigger={refreshTrigger} newExpenseIds={newExpenseIds} onClearNewExpenseIds={clearNewExpenseIds} />;
      case 'admin':
        return isAdmin(user) ? <AdminPanel /> : <ExpenseList refreshTrigger={refreshTrigger} newExpenseIds={newExpenseIds} onClearNewExpenseIds={clearNewExpenseIds} />;
      default:
        return <ExpenseList refreshTrigger={refreshTrigger} newExpenseIds={newExpenseIds} onClearNewExpenseIds={clearNewExpenseIds} />;
    }
  };

  return (
    <>
      {showEmailPrompt && (
        <TelegramEmailPrompt
          onLinkEmail={handleLinkEmail}
          onDismiss={() => setShowEmailPrompt(false)}
        />
      )}
      {mergeState && (
        <MergeConfirmModal
          conflictEmail={mergeState.conflictEmail}
          onConfirm={handleMergeConfirm}
          onCancel={() => setMergeState(null)}
        />
      )}
      <Layout
        activeTab={activeTab}
        onTabChange={setActiveTab}
        serverStatus={serverStatus}
      >
        <ErrorBoundary>
          {renderContent()}
        </ErrorBoundary>
      </Layout>
    </>
  );
}

export default App;
